// Icon picker for a product's `icon_key`. Shows a searchable grid of curated
// Material Symbols glyphs; the search box reaches the full Material Symbols set
// (including aliases), so any valid glyph can be found and selected. `icon_key`
// is rendered on the public form via the Material Symbols ligature font, so the
// value is simply the glyph name string. Controlled: `value` is the current
// key, `onChange` receives the new key (or '' when cleared).
import { useState, useEffect } from 'react';
import {
  Box, Button, TextField, Typography, Tooltip,
} from '@mui/material';
import { materialSymbolSx } from '../../theme.js';
import { PRODUCT_ICON_KEYS } from '../../config/product-icon-registry.js';
import { loadIconCatalog, searchIcons } from '../../config/icon-search.js';

// A single Material Symbols glyph rendered from its ligature name.
const Glyph = ({ name, fontSize = 24 }) => (
  <Box component="span" aria-hidden="true" sx={{ ...materialSymbolSx, fontSize }}>
    {name}
  </Box>
);

const IconPicker = ({ value, onChange }) => {
  const [query, setQuery] = useState('');
  const [catalog, setCatalog] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const normalized = query.trim().toLowerCase();
  const searching = normalized.length > 0;

  // Pull in the full catalog the first time the admin searches.
  useEffect(() => {
    if (!searching || catalog) return undefined;
    let active = true;
    loadIconCatalog()
      .then((rows) => { if (active) setCatalog(rows); })
      .catch(() => { if (active) setLoadFailed(true); });
    return () => { active = false; };
  }, [searching, catalog]);

  let matches;
  if (!searching) {
    matches = PRODUCT_ICON_KEYS;
  } else if (catalog) {
    matches = searchIcons(catalog, normalized);
  } else {
    // While the catalog loads (or if it failed), fall back to the curated set.
    matches = PRODUCT_ICON_KEYS.filter((key) => key.includes(normalized));
  }

  return (
    <Box>
      <Typography component="span" variant="subtitle2" sx={{ display: 'block', mb: 1 }}>
        Icon
      </Typography>

      <TextField
        size="small"
        fullWidth
        label="Search all icons"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        helperText={searching
          ? 'Searching every Material Symbol by name and keyword (e.g. “house”, “building”).'
          : 'Type to search the full Material Symbols set, including aliases.'}
        sx={{ mb: 1.5 }}
      />

      <Box
        role="listbox"
        aria-label="Product icons"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))',
          gap: 0.5,
          maxHeight: 200,
          overflowY: 'auto',
          p: 0.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {matches.map((key) => {
          const selected = key === value;
          return (
            <Tooltip key={key} title={key} disableInteractive>
              <Box
                component="button"
                type="button"
                role="option"
                aria-selected={selected}
                aria-label={key}
                onClick={() => onChange(selected ? '' : key)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 44,
                  minHeight: 44,
                  cursor: 'pointer',
                  borderRadius: 1,
                  border: 2,
                  borderColor: selected ? 'primary.main' : 'transparent',
                  bgcolor: selected ? 'action.selected' : 'transparent',
                  color: 'text.primary',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                }}
              >
                <Glyph name={key} />
              </Box>
            </Tooltip>
          );
        })}
        {matches.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1', p: 1 }}>
            {searching && !catalog && !loadFailed
              ? 'Loading the full icon set…'
              : 'No icons match. Enter a glyph name below instead.'}
          </Typography>
        )}
      </Box>

      {value && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mt: 1.5,
        }}
        >
          <Typography variant="body2" color="text.secondary">Selected:</Typography>
          <Glyph name={value} fontSize={22} />
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
            {value}
          </Typography>
          <Button
            size="small"
            onClick={() => onChange('')}
            sx={{ ml: 'auto' }}
          >
            Clear
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default IconPicker;
